import { SETTINGS_PERMISSIONS } from '@/constants/permissions';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useLocalListSearch } from '@/hooks/useLocalListSearch';
import { instituteTypesQueryOptions } from '../api/instituteTypes.api';
import { useInstituteTypeActionModals } from '../hooks/useInstituteTypeActionModals';
import type { InstituteType } from '../types/instituteType.types';
import { getInstituteTypeColumns } from './InstituteTypeColumns';
import { InstituteTypeDeleteConfirmation } from './InstituteTypeDeleteConfirmation';
import { InstituteTypeFormModal } from './InstituteTypeFormModal';

const route = getRouteApi('/_auth/settings/institute-types');

const toSearchableValues = (instituteType: InstituteType) => [
  instituteType.name,
  instituteType.code,
];

export function InstituteTypeListTable() {
  const { q: appliedSearchText } = route.useSearch();
  const navigate = route.useNavigate();

  const {
    modalState,
    instituteTypeToDelete,
    openCreateModal,
    openEditModal,
    closeModal,
    openDeleteModal,
    closeDeleteModal,
  } = useInstituteTypeActionModals();

  const {
    data: instituteTypes,
    isLoading,
    error,
    refetch,
  } = useQuery(instituteTypesQueryOptions());

  const { searchText, setSearchText, visibleRows } = useLocalListSearch({
    rows: instituteTypes,
    appliedText: appliedSearchText,
    onApply: (text) => void navigate({ search: { q: text }, replace: true }),
    toSearchableValues,
  });

  const columns = useMemo(
    () =>
      getInstituteTypeColumns(appliedSearchText, {
        onEdit: openEditModal,
        onDelete: openDeleteModal,
      }),
    [appliedSearchText, openEditModal, openDeleteModal],
  );

  return (
    <>
      <div className={FIT_TO_SCREEN_PAGE_CLASS}>
        <PageHeader
          title="Institute Types"
          search={{
            value: searchText,
            onChange: setSearchText,
            placeholder: 'Search by name or code',
          }}
          createAction={{
            label: 'Add Institute Type',
            onClick: openCreateModal,
            requires: SETTINGS_PERMISSIONS.INSTITUTE_TYPES_CREATE,
          }}
        />

        {error ? (
          <ErrorState
            error={error}
            fallbackTitle="Could not load institute types"
            onRetry={() => void refetch()}
            fill
          />
        ) : (
          <DataTable
            data={visibleRows}
            columns={columns}
            isLoading={isLoading}
            stickyColumnId="name"
            className="lg:flex-1"
          />
        )}
      </div>

      <InstituteTypeFormModal
        instituteType={modalState?.instituteType ?? null}
        open={modalState !== null}
        onOpenChange={(open) => !open && closeModal()}
      />

      <InstituteTypeDeleteConfirmation
        instituteType={instituteTypeToDelete}
        open={instituteTypeToDelete !== null}
        onOpenChange={(open) => !open && closeDeleteModal()}
      />
    </>
  );
}
